import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { loucaApi, buildImageUrl } from '../services/loucaApi';

const InspectionV2Real = () => {
  const [video, setVideo] = useState(null);
  const [tags, setTags] = useState('[]');
  const [strategy, setStrategy] = useState('KMEANS');
  const [enableLLM, setEnableLLM] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [imageErrors, setImageErrors] = useState(new Set());
  const [imageLoading, setImageLoading] = useState(new Set());
  const [history, setHistory] = useState([]);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
        const data = await loucaApi.listInspectionsV2();
        if (data.inspections) {
            setHistory(data.inspections);
        }
    } catch (e) {
        console.error("Error loading history", e);
    }
  };

  const handleHistoryClick = async (id) => {
      setLoading(true);
      setResult(null);
      try {
          const data = await loucaApi.getInspectionV2(id);
          if (data.status === 'success' || data.status === 'completed') {
              setResult(data.data);
              window.scrollTo({ top: 0, behavior: 'smooth' });
          } else {
              setError(data.message || "Erreur chargement historique");
          }
      } catch (e) {
          setError("Erreur chargement historique: " + e.message);
      } finally {
          setLoading(false);
      }
  };

  const handleVideoChange = (e) => {
    setVideo(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!video) {
        setError("Veuillez sélectionner une vidéo.");
        return;
    }
    
    setLoading(true);
    setError(null);
    setResult(null);
    setImageErrors(new Set());
    setImageLoading(new Set());

    const formData = new FormData();
    formData.append('video', video);
    formData.append('tags', tags);
    formData.append('strategy', strategy);
    formData.append('enable_llm', enableLLM);

    try {
      const data = await loucaApi.analyzeVehicleV2(formData);
      
      if (data.status === 'processing') {
        setResult(data.data);
        // Start polling
        const pollInterval = setInterval(async () => {
            try {
                const pollData = await loucaApi.getInspectionV2(data.inspection_id);
                if (pollData.status === 'success' || pollData.status === 'completed') {
                    clearInterval(pollInterval);
                    setLoading(false);
                    setResult(pollData.data);
                    loadHistory();
                } else if (pollData.status === 'failed') {
                    clearInterval(pollInterval);
                    setLoading(false);
                    setError("Erreur analyse: " + pollData.error);
                } else {
                    // Still processing, update result with intermediate data if available
                    if (pollData.data) {
                        setResult(prev => ({...prev, ...pollData.data}));
                    }
                }
            } catch (err) {
                console.error("Poll error", err);
                // Don't stop polling on transient errors
            }
        }, 3000);
      } else if (data.status === 'success') {
        setResult(data.data);
        setLoading(false);
        loadHistory();
      } else {
        setError("Erreur lors de l'analyse: " + JSON.stringify(data));
        setLoading(false);
      }
    } catch (err) {
      console.error(err);
      setError("Erreur API: " + err.message);
      setLoading(false);
    }
  };

  const addExampleTag = () => {
    const example = [
        { timestamp: 2.5, x: 0.5, y: 0.5 },
        { timestamp: 5.0, x: 0.2, y: 0.8 }
    ];
    setTags(JSON.stringify(example, null, 2));
  };

  const processedDamages = result?.llm_results?.processed_damages || [];
  const processedParts = result?.llm_results?.processed_parts || [];
  const fallbackFindings = result?.llm_results?.processed_findings || [];
  const missingViews = result?.llm_results?.missing_views || [];
  
  // Flatten legacy parts into damages list
  const legacyDamages = processedParts.flatMap(p => p.damages.map(d => ({...d, part_name: p.part_name})));
  const allDamages = [...processedDamages, ...legacyDamages];
  
  const hasStructuredLLM = allDamages.length > 0;
  const videoMetadata = result?.video_metadata || {};
  const videoName = result?.video_filename || video?.name || 'Vidéo';
  const imageCount = result?.image_count ?? (result?.manifest?.length ?? 0);
  
  const totalDamages = hasStructuredLLM
    ? allDamages.length
    : fallbackFindings.length;
    
  const totalOccurrences = hasStructuredLLM
    ? allDamages.reduce((acc, dmg) => acc + (dmg.occurrences?.length || 0), 0)
    : fallbackFindings.length;

  const totalCost = (() => {
    if (result?.llm_results?.total_estimated_cost != null) {
      return result.llm_results.total_estimated_cost;
    }
    if (fallbackFindings.length > 0) {
      return fallbackFindings.reduce((sum, finding) => sum + (finding.estimated_cost || finding.cost || 0), 0);
    }
    return 0;
  })();
  const hasLLMData = !!(result?.llm_results && (hasStructuredLLM || fallbackFindings.length > 0));
  const formatDuration = (seconds) => {
    if (seconds == null) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}m ${secs.toString().padStart(2, '0')}s`;
  };

  return (
    <Layout>
      <div className="pb-24 max-w-7xl mx-auto p-4">
        <div className="flex flex-col lg:flex-row gap-8">
            
            {/* MAIN CONTENT */}
            <div className="flex-1">
                <h1 className="text-3xl font-bold text-white mb-6">Inspection V2.1 (Engine Test)</h1>
                
                <div className="bg-slate-800 rounded-lg p-6 mb-6">
            <form onSubmit={handleSubmit} className="space-y-6">
                
                {/* VIDEO UPLOAD */}
                <div>
                    <label className="block text-white font-semibold mb-2">Vidéo (Tour du véhicule)</label>
                    <input
                        type="file"
                        accept="video/*"
                        onChange={handleVideoChange}
                        className="w-full text-white"
                    />
                </div>

                {/* STRATEGY SELECTOR */}
                <div>
                    <label className="block text-white font-semibold mb-2">Stratégie d'Extraction</label>
                    <select 
                        value={strategy} 
                        onChange={(e) => setStrategy(e.target.value)}
                        className="w-full px-4 py-2 bg-slate-700 text-white rounded"
                    >
                        <option value="KMEANS">Diversité (K-Means Clustering)</option>
                        <option value="SSIM">Mouvement (SSIM Séquentiel)</option>
                    </select>
                </div>

                {/* LLM TOGGLE */}
                <div className="flex items-center p-4 bg-slate-700/50 rounded border border-slate-600">
                    <input 
                        type="checkbox" 
                        id="enableLLM"
                        checked={enableLLM}
                        onChange={(e) => setEnableLLM(e.target.checked)}
                        className="w-5 h-5 text-blue-600 rounded focus:ring-blue-600 ring-offset-gray-800 focus:ring-2 bg-gray-700 border-gray-600"
                    />
                    <label htmlFor="enableLLM" className="ml-3 text-white font-semibold cursor-pointer select-none">
                        Activer l'analyse IA (LLM - Gemini 2.5)
                        <span className="block text-xs text-slate-400 font-normal mt-1">Détecte les dommages et génère des annotations visuelles.</span>
                    </label>
                </div>

                {/* TAGS JSON EDITOR */}
                <div>
                    <div className="flex justify-between items-center mb-2">
                        <label className="block text-white font-semibold">Tags / Zooms (JSON)</label>
                        <button type="button" onClick={addExampleTag} className="text-xs text-blue-400 underline">Exemple</button>
                    </div>
                    <textarea
                        value={tags}
                        onChange={(e) => setTags(e.target.value)}
                        className="w-full h-32 px-4 py-2 bg-slate-700 text-white font-mono text-sm rounded"
                    />
                    <p className="text-xs text-slate-400 mt-1">Format: [&#123;"timestamp": seconds, "x": 0..1, "y": 0..1&#125;]</p>
                </div>

                {/* SUBMIT */}
                <button
                    type="submit"
                    disabled={loading || !video}
                    className="w-full py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded disabled:opacity-50"
                >
                    {loading ? 'Traitement en cours...' : 'Lancer Analyse V2.1'}
                </button>

                {error && (
                    <div className="p-4 bg-red-900/50 text-red-200 rounded border border-red-700">
                        {error}
                    </div>
                )}
            </form>
        </div>

        {/* RESULTS DISPLAY */}
        {result && (
            <div className="space-y-12">
                
                {/* SUMMARY / SYNTHÈSE */}
                <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                        <div>
                            <h2 className="text-2xl text-white font-bold">📊 Synthèse de l'Inspection</h2>
                            <p className="text-sm text-slate-400 mt-1 break-all">
                                Vidéo : <span className="font-mono text-slate-200">{videoName}</span>
                                {videoMetadata.resolution && (
                                    <span className="ml-2 text-slate-500">({videoMetadata.resolution})</span>
                                )}
                            </p>
                        </div>
                        <div className="text-right text-sm text-slate-400">
                            <div>Durée : <span className="text-white">{formatDuration(videoMetadata.duration_seconds)}</span></div>
                            {videoMetadata.fps && (
                                <div>FPS : <span className="text-white">{videoMetadata.fps}</span></div>
                            )}
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="bg-slate-900/50 p-4 rounded">
                            <div className="text-sm text-slate-400 mb-1">Stratégie</div>
                            <div className="text-lg font-bold text-white">{result.strategy}</div>
                        </div>
                        <div className="bg-slate-900/50 p-4 rounded">
                            <div className="text-sm text-slate-400 mb-1">Résolution Vidéo</div>
                            <div className="text-lg font-bold text-white">{videoMetadata.resolution || 'N/A'}</div>
                        </div>
                        <div className="bg-slate-900/50 p-4 rounded">
                            <div className="text-sm text-slate-400 mb-1">Taille Vidéo</div>
                            <div className="text-lg font-bold text-white">{videoMetadata.size || 'N/A'}</div>
                        </div>
                        <div className="bg-slate-900/50 p-4 rounded">
                            <div className="text-sm text-slate-400 mb-1">Durée Vidéo</div>
                            <div className="text-lg font-bold text-white">{formatDuration(videoMetadata.duration_seconds)}</div>
                        </div>
                        <div className="bg-slate-900/50 p-4 rounded">
                            <div className="text-sm text-slate-400 mb-1">Images extraites</div>
                            <div className="text-lg font-bold text-white">{imageCount || 'N/A'}</div>
                        </div>
                        <div className="bg-slate-900/50 p-4 rounded">
                            <div className="text-sm text-slate-400 mb-1">Dégâts principaux</div>
                            <div className="text-lg font-bold text-white">{totalDamages || 0}</div>
                        </div>
                        <div className="bg-slate-900/50 p-4 rounded">
                            <div className="text-sm text-slate-400 mb-1">Occurrences annotées</div>
                            <div className="text-lg font-bold text-white">{totalOccurrences || 0}</div>
                        </div>
                        <div className="bg-slate-900/50 p-4 rounded">
                            <div className="text-sm text-slate-400 mb-1">Coût Total Estimé</div>
                            <div className="text-lg font-bold text-green-400">
                                {totalCost > 0 ? `${totalCost.toFixed(2)} €` : 'N/A'}
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* MISSING VIEWS */}
                {missingViews.length > 0 && (
                    <div className="bg-orange-900/30 border border-orange-700 rounded-lg p-6 mb-6">
                        <h2 className="text-xl font-bold text-orange-200 mb-4 flex items-center gap-2">
                            ⚠️ Vues Manquantes Identifiées
                        </h2>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-sm text-slate-300">
                                <thead className="bg-orange-900/50 text-orange-200 uppercase text-xs">
                                    <tr>
                                        <th className="px-4 py-2">Vue manquante</th>
                                        <th className="px-4 py-2">Raison détectée</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-orange-800/50">
                                    {missingViews.map((view, idx) => (
                                        <tr key={idx}>
                                            <td className="px-4 py-2 font-semibold">{view.view_name || view}</td>
                                            <td className="px-4 py-2 italic">{view.reason || '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* LLM FINDINGS */}
                {hasLLMData && (
                    <div>
                        <h2 className="text-2xl text-white font-bold mb-6 border-b border-slate-700 pb-2">
                            🔍 Dommages détectés ({totalDamages} dégât(s), {totalOccurrences} occurrence(s))
                        </h2>
                        {hasStructuredLLM ? (
                            <div className="space-y-8">
                                {allDamages.map((damage, dmgIdx) => (
                                    <div key={`damage-${dmgIdx}`} className="bg-slate-800 rounded-lg border border-slate-700 shadow-lg overflow-hidden">
                                        <div className="p-4 flex flex-col lg:flex-row lg:justify-between gap-4 border-b border-slate-700 bg-slate-800/80">
                                            <div>
                                                <p className="text-xs uppercase text-slate-400 tracking-wider mb-1">
                                                    {damage.part_name || 'Dégât détecté'}
                                                </p>
                                                <h4 className="text-white text-xl font-bold flex items-center gap-2">
                                                    {damage.damage_type}
                                                    <span className={`text-xs px-2 py-0.5 rounded-full ${damage.severity === 'HIGH' ? 'bg-red-900 text-red-200' : damage.severity === 'MEDIUM' ? 'bg-orange-900 text-orange-200' : 'bg-blue-900 text-blue-200'}`}>
                                                        {damage.severity}
                                                    </span>
                                                </h4>
                                                <p className="text-sm text-slate-300 mt-2">{damage.primary_description || damage.description}</p>
                                            </div>
                                            <div className="text-sm text-slate-400 flex flex-col gap-1 min-w-[150px]">
                                                <div className="flex justify-between">
                                                    <span>Certitude</span>
                                                    <span className="text-white font-semibold">
                                                        {damage.confidence != null ? `${(damage.confidence * 100).toFixed(0)}%` : 'N/A'}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span>Coût estimé</span>
                                                    <span className="text-green-400 font-semibold">
                                                        {damage.estimated_cost ? `${damage.estimated_cost.toFixed(2)} €` : 'N/A'}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span>Occurrences</span>
                                                    <span className="text-white font-semibold">{damage.occurrences?.length || 0}</span>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Occurrences Grid */}
                                        <div className="p-4 bg-slate-900/30">
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                                {damage.occurrences?.map((occ, occIdx) => {
                                                    const annotatedUrl = occ.annotated_image_url ? buildImageUrl(occ.annotated_image_url) : null;
                                                    return (
                                                        <div key={`occ-${dmgIdx}-${occIdx}`} className="bg-slate-900/60 border border-slate-700 rounded-lg p-3 hover:border-slate-500 transition-colors">
                                                            <div className="flex justify-between items-center text-xs text-slate-400 mb-2 border-b border-slate-800 pb-2">
                                                                <span className="font-mono text-blue-300">{occ.image_id}</span>
                                                                <span className="bg-slate-800 px-1.5 rounded text-slate-300">
                                                                    {occ.grid_cells && occ.grid_cells.length ? occ.grid_cells.join(', ') : 'N/A'}
                                                                </span>
                                                            </div>
                                                            
                                                            {annotatedUrl && (
                                                                <a 
                                                                    href={annotatedUrl}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="block group relative overflow-hidden rounded"
                                                                >
                                                                    <img 
                                                                        src={annotatedUrl}
                                                                        alt={`${damage.damage_type}`}
                                                                        className="w-full h-48 object-contain bg-black group-hover:scale-105 transition-transform duration-300"
                                                                        loading="lazy"
                                                                    />
                                                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                                                                        <span className="opacity-0 group-hover:opacity-100 bg-black/70 text-white text-xs px-2 py-1 rounded">
                                                                            Agrandir
                                                                        </span>
                                                                    </div>
                                                                </a>
                                                            )}

                                                            <div className="mt-3 space-y-1">
                                                                {occ.description && (
                                                                    <p className="text-sm text-slate-300 italic">"{occ.description}"</p>
                                                                )}
                                                                <div className="flex justify-between text-[10px] text-slate-500 pt-2">
                                                                     <span>Conf: {occ.confidence != null ? `${(occ.confidence * 100).toFixed(0)}%` : '-'}</span>
                                                                     <span>Res: {occ.annotated_image_resolution || '-'}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {fallbackFindings.map((finding, idx) => {
                                    const imageUrl = buildImageUrl(finding.annotated_image_url);
                                    return (
                                        <div key={`legacy-finding-${idx}`} className="bg-slate-800 rounded-lg overflow-hidden shadow-lg border border-slate-700">
                                            <div className="relative">
                                                {imageUrl && (
                                                    <a href={imageUrl} target="_blank" rel="noopener noreferrer" title="Ouvrir dans un nouvel onglet">
                                                        <img 
                                                            src={imageUrl} 
                                                            alt={finding.damage_type}
                                                            className="w-full h-64 object-contain bg-black hover:opacity-90 transition-opacity"
                                                            loading="lazy"
                                                        />
                                                    </a>
                                                )}
                                                <div className="absolute top-2 right-2 bg-red-600 text-white text-xs font-bold px-2 py-1 rounded">
                                                    {finding.severity}
                                                </div>
                                            </div>
                                            <div className="p-4">
                                                <h3 className="text-lg font-bold text-white mb-1">{finding.damage_type}</h3>
                                                <p className="text-sm text-blue-400 mb-2">
                                                    Grid: {finding.grid_cells && finding.grid_cells.length ? finding.grid_cells.join(', ') : (finding.grid_location || 'N/A')}
                                                </p>
                                                <p className="text-sm text-slate-300 mb-3">{finding.description}</p>
                                                <div className="space-y-2 mb-3 text-xs text-slate-400">
                                                    <div className="flex justify-between">
                                                        <span>Certitude</span>
                                                        <span className="text-white font-semibold">{finding.confidence != null ? `${(finding.confidence * 100).toFixed(0)}%` : 'N/A'}</span>
                                                    </div>
                                                    {finding.cost != null && (
                                                        <div className="flex justify-between">
                                                            <span>Coût estimé</span>
                                                            <span className="text-green-400 font-semibold">{finding.cost.toFixed(2)} €</span>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="text-xs text-slate-500 border-t border-slate-700 pt-3 flex justify-between">
                                                    <span>ID: {finding.image_id}</span>
                                                    <span>Résolution: {finding.annotated_image_resolution || 'N/A'}</span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* GLOBALS */}
                <div>
                    <h2 className="text-xl text-white font-bold mb-4">Vues Globales ({strategy})</h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {result.manifest.filter(m => m.type === 'global_view').map((img, idx) => {
                            const imageUrl = buildImageUrl(img.url);
                            const imageKey = img.image_id || img.file || `global-${idx}`;
                            const hasError = imageErrors.has(imageKey);
                            const isLoading = imageLoading.has(imageKey);
                            
                            return (
                                <div key={`global-${imageKey}`} className="bg-slate-800 p-2 rounded relative min-h-[200px]">
                                    {isLoading && !hasError && (
                                        <div className="absolute inset-0 flex items-center justify-center bg-slate-700 rounded z-10">
                                            <div className="text-slate-400 text-xs">Chargement...</div>
                                        </div>
                                    )}
                                    {hasError && (
                                        <div className="absolute inset-0 flex items-center justify-center bg-red-900/50 rounded border-2 border-red-500 z-10">
                                            <div className="text-red-300 text-xs text-center p-2">
                                                Erreur de chargement<br/>
                                                <a href={imageUrl} target="_blank" rel="noopener noreferrer" className="underline">
                                                    Ouvrir directement
                                                </a>
                                            </div>
                                        </div>
                                    )}
                                    <img 
                                        src={imageUrl} 
                                        alt={img.file}
                                        className={`w-full h-auto rounded ${hasError ? 'opacity-50' : ''}`}
                                        loading={idx < 12 ? "eager" : "lazy"}
                                        onError={(e) => {
                                            console.error(`❌ Erreur chargement image ${imageKey}:`, imageUrl);
                                            setImageErrors(prev => new Set([...prev, imageKey]));
                                            setImageLoading(prev => {
                                                const next = new Set(prev);
                                                next.delete(imageKey);
                                                return next;
                                            });
                                        }}
                                        onLoad={(e) => {
                                            console.log(`✅ Image chargée: ${imageKey}`);
                                            setImageLoading(prev => {
                                                const next = new Set(prev);
                                                next.delete(imageKey);
                                                return next;
                                            });
                                        }}
                                        onLoadStart={() => {
                                            setImageLoading(prev => new Set([...prev, imageKey]));
                                        }}
                                    />
                                    <p className="text-xs text-center text-slate-300 mt-2 font-mono">{img.file}</p>
                                    <p className="text-[11px] text-center text-slate-500">ID: {img.image_id || 'N/A'} · Résolution: {(img.width && img.height) ? `${img.width}x${img.height}` : 'N/A'}</p>
                                    <a 
                                        href={imageUrl} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-xs text-center text-blue-400 hover:text-blue-300 block mt-1 break-all"
                                        title="Ouvrir l'image dans un nouvel onglet"
                                    >
                                        {imageUrl}
                                    </a>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* DETAILS / ZOOMS */}
                <div>
                    <h2 className="text-xl text-white font-bold mb-4">Zooms / Détails Composites</h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {result.manifest.filter(m => m.type === 'detail_composite').map((img, idx) => {
                            const imageUrl = buildImageUrl(img.url);
                            const imageKey = img.image_id || img.file || `detail-${idx}`;
                            return (
                                <div key={`detail-${imageKey}`} className="bg-slate-800 p-2 rounded">
                                    <img 
                                        src={imageUrl} 
                                        alt={img.file}
                                        key={`img-zoom-${imageKey}`}
                                        className="w-full h-auto rounded"
                                        loading="lazy"
                                        onError={(e) => {
                                            console.error(`❌ Erreur chargement zoom ${imageKey}:`, imageUrl);
                                            e.target.style.border = "2px solid red";
                                            e.target.alt = `ERREUR: ${imageKey}`;
                                        }}
                                        onLoad={() => console.log(`✅ Zoom chargé: ${imageKey}`)}
                                    />
                                    <p className="text-xs text-center text-slate-300 mt-2 font-mono">{img.file}</p>
                                    <p className="text-[11px] text-center text-slate-500">ID: {img.image_id || 'N/A'} · Résolution: {(img.width && img.height) ? `${img.width}x${img.height}` : 'N/A'}</p>
                                </div>
                            );
                        })}
                    </div>
                    {result.manifest.filter(m => m.type === 'detail_composite').length === 0 && (
                        <p className="text-slate-400 italic">Aucun zoom généré (ajoutez des tags JSON).</p>
                    )}
                </div>
                
                <div className="bg-slate-900 p-4 rounded text-xs text-slate-500 font-mono overflow-x-auto">
                    DEBUG: {JSON.stringify(result, null, 2)}
                </div>
            </div>
        )}
      </div>

      {/* SIDEBAR HISTORY */}
      <div className="w-full lg:w-80 shrink-0">
          <div className="bg-slate-800 rounded-lg p-4 sticky top-4">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center justify-between">
                  Historique
                  <button onClick={loadHistory} className="text-xs text-blue-400 hover:text-blue-300 font-normal">Actualiser</button>
              </h2>
              
              {history.length === 0 ? (
                  <p className="text-slate-500 text-sm italic">Aucune inspection récente.</p>
              ) : (
                  <div className="space-y-3 max-h-[calc(100vh-200px)] overflow-y-auto pr-1">
                      {history.map(item => {
                          const dateStr = item.timestamp.length === 15 
                              ? `${item.timestamp.substring(0,4)}-${item.timestamp.substring(4,6)}-${item.timestamp.substring(6,8)} ${item.timestamp.substring(9,11)}:${item.timestamp.substring(11,13)}`
                              : item.timestamp;
                              
                          return (
                              <div 
                                  key={item.inspection_id}
                                  onClick={() => handleHistoryClick(item.inspection_id)}
                                  className="p-3 bg-slate-700/50 hover:bg-slate-700 border border-slate-600 rounded cursor-pointer transition-all hover:border-blue-500 group"
                              >
                                  <div className="flex justify-between items-start mb-1">
                                      <span className="text-xs font-mono text-slate-400">{dateStr}</span>
                                      <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${item.strategy === 'SSIM' ? 'bg-purple-900 text-purple-200' : 'bg-blue-900 text-blue-200'}`}>
                                          {item.strategy}
                                      </span>
                                  </div>
                                  <div className="text-sm font-bold text-white truncate mb-1" title={item.inspection_id}>
                                      {item.inspection_id}
                                  </div>
                                  <div className="flex items-center gap-2 text-xs">
                                      <span className={`flex items-center gap-1 ${item.status === 'processing' ? 'text-blue-400' : item.findings_count > 0 ? 'text-red-400' : 'text-green-400'}`}>
                                          {item.status === 'processing' ? '⏳ En cours...' : (item.findings_count > 0 ? `⚠️ ${item.findings_count} dommage(s)` : '✅ OK')}
                                      </span>
                                  </div>
                                  <div className="flex flex-wrap gap-2 text-xs text-slate-500 mt-1">
                                      {item.video_resolution && (
                                          <span>Rés: {item.video_resolution}</span>
                                      )}
                                      {typeof item.image_count === 'number' && (
                                          <span>Imgs: {item.image_count}</span>
                                      )}
                                      {item.total_estimated_cost != null && (
                                          <span>Coût: <span className="text-green-400">
                                              {item.total_estimated_cost > 0 ? `${Number(item.total_estimated_cost).toFixed(2)} €` : 'N/A'}
                                          </span></span>
                                      )}
                                  </div>
                              </div>
                          );
                      })}
                  </div>
              )}
          </div>
      </div>

    </div>
  </div>

</Layout>
  );
};

export default InspectionV2Real;
